import {
  type Answers,
  type QuestionKey,
  sortByQuestionOrder,
} from './constants';

type FormInputVisibleState =
  | 'active' // 普通に表示される
  | 'inactive' // 透過で表示される
  | 'hidden'; // 表示されない;

const getVisibleState = (
  selfAnswer: Nullable<unknown>,
  prevError: Maybe<ValidationError>,
  prevVisibleState: FormInputVisibleState
) => {
  if (selfAnswer !== null) {
    return prevError !== undefined
      ? 'inactive'
      : prevVisibleState === 'inactive'
        ? 'inactive'
        : 'active';
  }
  return prevError === undefined
    ? prevVisibleState === 'inactive'
      ? 'inactive'
      : 'active'
    : 'hidden';
};

export type AnswerVisibleStates = Record<keyof Answers, FormInputVisibleState>;
export const useDiagnosisVisibleState = (
  answers: Ref<Answers>,
  enabledQuestions: Ref<Record<QuestionKey, boolean>>,
  errorFields: Ref<ValidatedResult<Answers>>
): { formInputVisibleStates: ComputedRef<AnswerVisibleStates> } => {
  const enabledQuestionList = computed(() => {
    return sortByQuestionOrder(
      Object.entries(enabledQuestions.value)
        .filter(([_, enabled]) => enabled)
        .map(([question]) => question as QuestionKey)
    );
  });
  const disabledQuestionList = computed(() => {
    return sortByQuestionOrder(
      Object.entries(enabledQuestions.value)
        .filter(([_, enabled]) => !enabled)
        .map(([question]) => question as QuestionKey)
    );
  });

  const formInputVisibleStates = computed<AnswerVisibleStates>(() => {
    const enabledVisibleStates = enabledQuestionList.value.reduce<
      Partial<Record<QuestionKey, FormInputVisibleState>>
    >((acc, questionKey, index, wholeQustionKeys) => {
      if (index === 0) {
        return {
          ...acc,
          [questionKey]: 'active',
        };
      }
      const prevQuestionKey = wholeQustionKeys[index - 1];
      const prevVisibleState = acc[prevQuestionKey];
      if (prevVisibleState == undefined) {
        // 基本通らないけど、型チェック通すために分岐
        return {
          ...acc,
          [questionKey]: 'hidden',
        };
      }
      return {
        ...acc,
        [questionKey]: getVisibleState(
          answers.value[questionKey],
          errorFields.value[prevQuestionKey],
          prevVisibleState
        ),
      };
    }, {});

    const disabledVisibleStates = Object.fromEntries(
      disabledQuestionList.value.map((questionKey) => {
        return [questionKey, 'hidden'] as const;
      })
    );

    return {
      ...enabledVisibleStates,
      ...disabledVisibleStates,
    } as AnswerVisibleStates;
  });

  return {
    formInputVisibleStates,
  };
};
